
import { sequences2025 } from './sequences/sequences2025';
import { sequences2026 } from './sequences/sequences2026';
import { sequences2027 } from './sequences/sequences2027';
import { sequences2028 } from './sequences/sequences2028';
import { sequences2029 } from './sequences/sequences2029';
import { sequences2030 } from './sequences/sequences2030';
import { sequences2031 } from './sequences/sequences2031';
import { sequences2032 } from './sequences/sequences2032';
import { sequences2033 } from './sequences/sequences2033';
import { sequences2034 } from './sequences/sequences2034';
import { getCookie, setCookie } from "./cookieUtils";
import { WordSequence } from './types/WordSequence';
import { clearGameState } from '@/components/game/GamePersistenceV2';
import { getDebugParams, isDebugDateMode } from './debugUtils';
import { toast } from "sonner";
import { getEffectiveDate, normalizeDate, handleNewDay } from './dateUtils';
import { WordSelectionStrategyFactory } from './wordSelection/StrategyFactory';
import { getDayOfYear } from './dateUtils';

// Cache word sequences to prevent repeated calculations
const sequencesCache = new Map<number, WordSequence>();

const yearlySequences: Record<number, WordSequence> = {
  2025: sequences2025,
  2026: sequences2026,
  2027: sequences2027,
  2028: sequences2028,
  2029: sequences2029,
  2030: sequences2030,
  2031: sequences2031,
  2032: sequences2032,
  2033: sequences2033,
  2034: sequences2034,
};

export const getYearlySequences = (year: number): WordSequence => {
  // Check cache first for performance
  if (sequencesCache.has(year)) {
    return sequencesCache.get(year)!;
  }
  
  // Normalize year to available sequences (2025-2034)
  const normalizedYear = 2025 + ((year - 2025) % 10);
  
  // Cache and return result
  const result = yearlySequences[normalizedYear];
  sequencesCache.set(year, result);
  
  return result;
};

export { getEffectiveDate, getDayOfYear, handleNewDay };

// Enhanced getWordForDay that uses strategy pattern
export const getWordForDay = (date: Date = getEffectiveDate()): string[] => {
  // Always work with normalized dates to ensure consistency
  const normalizedDate = normalizeDate(date);
  
  // Check for forced mode
  const forcedMode = localStorage.getItem('babblegram_force_mode');
  const currentMode = forcedMode || localStorage.getItem('babblegram_game_mode') || 'daily';
  
  console.log('🎯 getWordForDay called with mode:', {
    currentMode,
    forcedMode,
    storedMode: localStorage.getItem('babblegram_game_mode'),
    date: normalizedDate.toISOString(),
    timestamp: new Date().toISOString()
  });
  
  // If we had a forced mode, clear it after reading
  if (forcedMode) {
    console.log('Found forced mode:', forcedMode);
    localStorage.removeItem('babblegram_force_mode');
  }
  
  // Check for play again flag - this should trigger a strategy reset
  const isPlayAgain = localStorage.getItem('babblegram_play_again') === 'true';
  
  if (isPlayAgain) {
    console.log('🎮 PLAY AGAIN detected - forcing strategy reset', {
      mode: currentMode,
      timestamp: new Date().toISOString()
    });
    
    // Clear the flag
    localStorage.removeItem('babblegram_play_again');
    
    // Reset the strategy to force new sequence generation
    WordSelectionStrategyFactory.resetStrategy(currentMode);
  }
  
  // Get the appropriate strategy based on the current game mode
  const strategy = WordSelectionStrategyFactory.getStrategy(currentMode);
  
  console.log('Using word selection strategy:', {
    mode: currentMode,
    strategyId: strategy.getStrategyId(),
    date: normalizedDate.toISOString(),
    timestamp: new Date().toISOString(),
    source: 'getWordForDay'
  });
  
  // Use the strategy to get the word sequence
  const sequence = strategy.getWordSequence(normalizedDate);
  
  // Validate the sequence
  if (!sequence || !Array.isArray(sequence) || sequence.length < 4) {
    console.error('Invalid sequence returned by strategy', {
      mode: currentMode,
      strategyId: strategy.getStrategyId(),
      sequence,
      timestamp: new Date().toISOString()
    });
    
    // Return a fallback sequence
    return ['cat', 'cats', 'caste', 'castle'];
  }
  
  return sequence;
};

export const getDailyStreak = (): number => {
  // Import here to avoid circular dependency
  const { modeAwareStorage } = require('@/lib/managers/ModeAwareStorageManager');
  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  
  if (currentMode === 'daily') {
    return modeAwareStorage.getStreak();
  }
  
  // For non-daily modes, return 0 since this function is specifically for daily streaks
  return 0;
};
